import { useState, useEffect, useRef } from 'react';
import { useDebouncedValue } from '../lib/useDebouncedValue';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { getMasters, getProjects, deleteMaster, MASTER_STATUSES, errorMessage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { LoadError } from '../components/LoadError';
import { TruncationNotice, totalFromHeaders } from '../components/TruncationNotice';
import { MasterCards } from '../components/MasterCards';
import { 
    Search, 
    HardDrive, 
    MoreVertical,
    Edit,
    Trash2,
    Printer,
    Lock,
    Film,
    ExternalLink,
    ArrowUpDown,
    ArrowUp,
    ArrowDown,
    Download
} from 'lucide-react';

export default function Masters() {
    const [masters, setMasters] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [total, setTotal] = useState(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [projectFilter, setProjectFilter] = useState('all');
    const [deleteId, setDeleteId] = useState(null);
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');
    const [exporting, setExporting] = useState(false);
    const { isManager, isAdmin } = useAuth();
    const navigate = useNavigate();
    const debouncedSearch = useDebouncedValue(search);
    // Only the latest request may update the list (typing fast must not show stale results)
    const requestId = useRef(0);

    // Projects only feed the filter and the "Projet" column: load them once
    useEffect(() => {
        getProjects()
            .then(res => setProjects(res.data))
            .catch(() => {});
    }, []);

    useEffect(() => {
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when these inputs change; the loader is recreated each render and reads current state
    }, [debouncedSearch, statusFilter, projectFilter]);

    const fetchData = async () => {
        const id = ++requestId.current;
        try {
            setLoadError(null);
            const mastersRes = await getMasters({
                search: debouncedSearch || undefined,
                status: statusFilter !== 'all' ? statusFilter : undefined,
                project_id: projectFilter !== 'all' ? projectFilter : undefined
            });
            if (id !== requestId.current) return;
            setMasters(mastersRes.data);
            setTotal(totalFromHeaders(mastersRes));
        } catch (error) {
            if (id !== requestId.current) return;
            if (!error._isAuthError) setLoadError(errorMessage(error, 'Erreur lors du chargement des masters'));
        } finally {
            if (id === requestId.current) setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        try {
            await deleteMaster(deleteId);
            toast.success('Master supprimé');
            setDeleteId(null);
            fetchData();
        } catch (error) {
            toast.error(errorMessage(error, 'Erreur lors de la suppression'));
        }
    };

    const getProjectTitle = (projectId) => {
        const project = projects.find(p => p.id === projectId);
        return project?.title || 'N/A';
    };

    const getStatusBadgeClass = (status) => {
        return `status-${status}`;
    };

    const handleExportCSV = async () => {
        setExporting(true);
        try {
            const params = new URLSearchParams();
            if (search) params.set('search', search);
            if (statusFilter !== 'all') params.set('status', statusFilter);
            if (projectFilter !== 'all') params.set('project_id', projectFilter);
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/masters/export/csv${params.toString() ? '?' + params.toString() : ''}`;
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('ad_master_token')}` }
            });
            const blob = await response.blob();
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = 'masters_export.csv';
            link.click();
            URL.revokeObjectURL(link.href);
            toast.success('Export CSV téléchargé');
        } catch (error) {
            toast.error(errorMessage(error, 'Erreur lors de l\'export'));
        } finally {
            setExporting(false);
        }
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const getSortIcon = (field) => {
        if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
        return sortDirection === 'asc' 
            ? <ArrowUp className="w-3 h-3 ml-1 text-primary" /> 
            : <ArrowDown className="w-3 h-3 ml-1 text-primary" />;
    };

    const getAriaSort = (field) => {
        if (sortField !== field) return 'none';
        return sortDirection === 'asc' ? 'ascending' : 'descending';
    };

    const sortedMasters = [...masters].sort((a, b) => {
        if (!sortField) return 0;
        const valA = a[sortField] || '';
        const valB = b[sortField] || '';
        // Natural sort for disk numbers (e.g. BT#9 < BT#88 < BT#100)
        const cmp = valA.localeCompare(valB, undefined, { numeric: true, sensitivity: 'base' });
        return sortDirection === 'asc' ? cmp : -cmp;
    });

    const renderMasterActions = (master) => (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label={`Actions pour ${master.name}`} className="h-8 w-8">
                    <MoreVertical className="w-4 h-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate(`/masters/${master.id}`)}>
                    <HardDrive className="w-4 h-4 mr-2" />
                    Détails
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate(`/masters/${master.id}/label`)}>
                    <Printer className="w-4 h-4 mr-2" />
                    Imprimer étiquette
                </DropdownMenuItem>
                {isManager() && (
                    <>
                        <DropdownMenuItem onClick={() => navigate(`/masters/${master.id}/edit`)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Modifier
                        </DropdownMenuItem>
                        {isAdmin() && (
                            <DropdownMenuItem 
                                onClick={() => setDeleteId(master.id)}
                                className="text-destructive-text focus:text-destructive-text"
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Supprimer
                            </DropdownMenuItem>
                        )}
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );

    return (
        <Layout>
            <div className="p-4 sm:p-6 space-y-6 animate-fade-in" data-testid="masters-page">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl font-bold tracking-tight">
                            MASTERS
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Tous les disques durs DCP
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <p className="text-sm text-muted-foreground" data-testid="masters-count">
                            <span className="font-mono text-foreground tabular-nums">{sortedMasters.length}</span>
                            {sortedMasters.length > 1 ? ' masters' : ' master'}
                        </p>
                        <Button 
                        variant="outline" 
                        onClick={handleExportCSV} 
                        disabled={exporting || masters.length === 0}
                        data-testid="export-csv-button"
                    >
                        <Download className="w-4 h-4 mr-2" />
                        {exporting ? 'Export...' : 'Export CSV'}
                    </Button>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Rechercher un master..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-10"
                            data-testid="master-search-input"
                        />
                    </div>
                    <Select value={projectFilter} onValueChange={setProjectFilter}>
                        <SelectTrigger className="w-full sm:w-[200px]" data-testid="project-filter">
                            <SelectValue placeholder="Tous les projets" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous les projets</SelectItem>
                            {projects.map(project => (
                                <SelectItem key={project.id} value={project.id}>
                                    {project.title}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
                            <SelectValue placeholder="Tous les statuts" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous les statuts</SelectItem>
                            {Object.entries(MASTER_STATUSES).map(([key, label]) => (
                                <SelectItem key={key} value={key}>{label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {!loading && !loadError && <TruncationNotice shown={masters.length} total={total} noun="masters" />}
                {/* Masters Table */}
                {loading ? (
                    <Skeleton className="h-96" />
                ) : loadError ? (
                    <LoadError message={loadError} onRetry={() => { setLoading(true); fetchData(); }} />
                ) : masters.length === 0 ? (
                    <Card className="bg-card border-border">
                        <CardContent className="flex flex-col items-center justify-center py-16">
                            <HardDrive className="w-16 h-16 text-muted-foreground/50 mb-4" />
                            <h3 className="font-heading text-xl font-semibold mb-2">
                                Aucun master
                            </h3>
                            <p className="text-muted-foreground text-center max-w-md">
                                {search || statusFilter !== 'all' || projectFilter !== 'all'
                                    ? 'Aucun master ne correspond à vos filtres.'
                                    : 'Créez un master depuis la page d\'un projet.'}
                            </p>
                        </CardContent>
                    </Card>
                ) : (
                    <>
                    <MasterCards
                        masters={sortedMasters}
                        statusClass={getStatusBadgeClass}
                        renderActions={renderMasterActions}
                        projectTitle={getProjectTitle}
                    />
                    <Card className="hidden sm:block bg-card border-border overflow-hidden">
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                                        <TableHead 
                                            className="font-bold text-xs uppercase tracking-wider"
                                            aria-sort={getAriaSort('name')}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => handleSort('name')}
                                                className="flex items-center uppercase tracking-wider font-bold select-none hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm"
                                                data-testid="sort-name"
                                            >
                                                Nom du master
                                                {getSortIcon('name')}
                                            </button>
                                        </TableHead>
                                        <TableHead className="font-bold text-xs uppercase tracking-wider">Projet</TableHead>
                                        <TableHead 
                                            className="font-bold text-xs uppercase tracking-wider"
                                            aria-sort={getAriaSort('disk_number')}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => handleSort('disk_number')}
                                                className="flex items-center uppercase tracking-wider font-bold select-none hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm"
                                                data-testid="sort-disk-number"
                                            >
                                                N° Disque
                                                {getSortIcon('disk_number')}
                                            </button>
                                        </TableHead>
                                        <TableHead className="font-bold text-xs uppercase tracking-wider">Durée</TableHead>
                                        <TableHead className="font-bold text-xs uppercase tracking-wider">Format</TableHead>
                                        <TableHead className="font-bold text-xs uppercase tracking-wider">Statut</TableHead>
                                        <TableHead className="font-bold text-xs uppercase tracking-wider w-[100px]">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {sortedMasters.map((master) => (
                                        <TableRow 
                                            key={master.id} 
                                            className="hover:bg-muted/30 cursor-pointer"
                                            onClick={() => navigate(`/masters/${master.id}`)}
                                            data-testid={`master-row-${master.id}`}
                                        >
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <Link
                                                        to={`/masters/${master.id}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                        title={master.name}
                                                        className="font-mono text-sm truncate max-w-[300px] hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm"
                                                    >
                                                        {master.name}
                                                    </Link>
                                                    {master.encrypted && (
                                                        <Lock className="w-3 h-3 text-tone-ecrase flex-shrink-0" />
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Link
                                                    to={`/projects/${master.project_id}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors"
                                                >
                                                    <Film className="w-3 h-3" />
                                                    {getProjectTitle(master.project_id)}
                                                </Link>
                                            </TableCell>
                                            <TableCell className="font-mono text-sm">{master.disk_number}</TableCell>
                                            <TableCell className="font-mono text-sm">{master.duration_tc}</TableCell>
                                            <TableCell className="text-sm">{master.format}</TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className={`text-xs ${getStatusBadgeClass(master.status)}`}>
                                                    {MASTER_STATUSES[master.status] || master.status}
                                                </Badge>
                                            </TableCell>
                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                {renderMasterActions(master)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </Card>
                    </>
                )}
            </div>

            {/* Delete Dialog */}
            <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer ce master ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Cette action est irréversible. Tous les mouvements associés seront également supprimés.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Supprimer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Layout>
    );
}
